import React, { Fragment } from 'react'
import { Route, Switch, Redirect } from 'react-router-dom'
import RouteSync from './RouteSync'

import mapRouterParams from './utils/mapRouterParams'

import Channels from 'components/Channels'
import NotesShow from 'screens/NotesShow'

import App from 'components/App'
import AppView from 'components/Layout/AppView'
import { NotePanelLoading } from 'components/Layout/AppLoading'

import AuthContainer from 'components/Auth/AuthContainer'
import AuthLogin from 'components/Auth/AuthLogin'
import AuthRequestResetPassword from 'components/Auth/AuthRequestResetPassword'
import AuthResetPassword from 'components/Auth/AuthResetPassword'
import AutoSignIn from 'components/Auth/AutoSignIn'

import PublicRoot from 'components/Public/Root'
import PublicNotesShow from 'screens/PublicNotesShow'
import PublicChannelsShow from 'screens/PublicChannelsShow'

import Logged from 'components/Logged'
import Settings from 'components/Settings'
import NotFound from 'components/Errors/NotFound'
import InAppNotFound from 'components/Errors/InAppNotFound'
import InAppForbidden from 'components/Errors/InAppForbidden'
import NoteForbidden from 'components/Errors/Note/Forbidden'
import NoteNotFound from 'components/Errors/Note/NotFound'
import Activity from 'components/Activity'
import TemplatePanel from 'components/Templates/Panel'
import TemplatesView from 'components/Templates/View'

import ChannelsShow from 'screens/ChannelsShow'
import FiltersShow from 'screens/FiltersShow'

import RedirectToRootNote from 'components/Notes/RedirectToRoot'
import OwnerOnboarding from 'screens/OwnerOnboarding'

import AppLoading from 'components/Layout/AppLoading'

const NotesShowRoute = mapRouterParams(NotesShow)
const TemplatesShowRoute = mapRouterParams(TemplatesView)
const SettingRoute = mapRouterParams(Settings)
const PublicNotesShowRoute = mapRouterParams(PublicNotesShow)
const PublicChannelsShowRoute = mapRouterParams(PublicChannelsShow)

const AppRoutes = React.memo(() => {
  return (
    <Fragment>
      <Switch>
        {/* Settings */}
        <Route
          path="/app/settings/:type/:tab?/:options?"
          component={SettingRoute}
        />
        {/* App */}
        <Route path="*">
          <AppView>
            <Switch>
              <Route
                path="/app/notes/:noteId/:options?"
                component={NotesShowRoute}
              />
              <Route
                path={[
                  '/app/channels/:channelId/collections/:collectionId',
                  '/app/channels/:channelId',
                ]}
              >
                {({ match }) => (
                  <Channels channelId={match.params.channelId}>
                    <Switch>
                      <Route
                        path={`${match.path}/notes/:noteId/:options?`}
                        component={NotesShowRoute}
                      />
                      <Route path={`${match.path}/notes`}>
                        <ChannelsShow
                          channelId={match.params.channelId}
                          goToNotes
                        />
                      </Route>
                      <Route path={match.path}>
                        <ChannelsShow channelId={match.params.channelId} />
                      </Route>
                      <Route path="*" component={NotePanelLoading} />
                    </Switch>
                  </Channels>
                )}
              </Route>
              {/* Templates */}
              <Route path="/app/templates">
                <TemplatePanel>
                  <Route
                    path="/app/templates/:templateId"
                    component={TemplatesShowRoute}
                  />
                </TemplatePanel>
              </Route>

              {/* Filters: all, bookmarked, archived */}
              <Route path={'/app/filters/:filterId'}>
                {({ match }) => (
                  <FiltersShow filterId={match.params.filterId}>
                    <Switch>
                      <Redirect
                        exact
                        from="/app/filters/:filterId"
                        to="/app/filters/:filterId/notes"
                      />
                      <Route
                        path={'/app/filters/:filterId/notes/:noteId/:options?'}
                        component={NotesShowRoute}
                      />
                      <Route path="*" component={NotePanelLoading} />
                    </Switch>
                  </FiltersShow>
                )}
              </Route>

              <Route exact path="/app/onboarding" component={OwnerOnboarding} />
              <Route exact path="/app/activity" component={Activity} />
              <Route exact path="/app/root" component={RedirectToRootNote} />
              <Route
                path="/app/notfound/note/:noteId"
                component={NoteNotFound}
              />
              <Route path="/app/forbidden" component={InAppForbidden} />
              <Route
                path="forbidden/note/:noteId/:personal?"
                component={NoteForbidden}
              />

              <Route path="*" component={InAppNotFound} />
            </Switch>
          </AppView>
        </Route>
      </Switch>
    </Fragment>
  )
})

function PublicRoutes() {
  return (
    <PublicRoot>
      <Switch>
        <Route component={PublicNotesShowRoute} path="/p/note/:token/:slug?" />
        <Route
          component={PublicChannelsShowRoute}
          path="/p/channel/:token/:slug?"
        />
        <Route path="*" component={NotFound} />
      </Switch>
    </PublicRoot>
  )
}

function AuthRoutes() {
  return (
    <AuthContainer>
      <Switch>
        <Route path="/auth/login" component={AuthLogin} />
        <Route
          path="/auth/request-reset-password"
          component={AuthRequestResetPassword}
        />
        <Route
          path="/auth/reset-password/:resetPasswordToken"
          component={mapRouterParams(AuthResetPassword)}
        />
      </Switch>
    </AuthContainer>
  )
}

const TopRoutes = React.memo(() => {
  return (
    <Switch>
      <Route path="/loading" component={AppLoading} />
      <Route path="/auth/autoSignIn" component={AutoSignIn} />
      <Route path="/p/*" component={PublicRoutes} />
      <Route path="/auth/*" component={AuthRoutes} />
      <Redirect exact from="/" to="/app/root" />
      <Redirect exact from="/app" to="/app/root" />
      <Redirect exact from="/app/notifications" to="/app/root" />
      <Redirect
        from="/public/share/note/:token/:slug?"
        to="/p/note/:token/:slug?"
      />
      <Route path="/app/*">
        <Logged>
          <AppRoutes />
        </Logged>
      </Route>
      <Route path="*" component={NotFound} />
    </Switch>
  )
})

export default React.memo(function Routes() {
  return (
    <App>
      <Fragment>
        <TopRoutes />
        <RouteSync />
      </Fragment>
    </App>
  )
})
